// Live cover patterns for the admin picker preview. MIRRORS the .hc-cover--*
// rules in app/frontend/entrypoints/help_center.css (the public render). Keep
// the keys + layers in sync with that file and HelpCenter::CoverPatterns::KEYS.

export const COVER_PATTERNS = [
  'dots', 'dots-lg', 'grid', 'diagonal', 'stripes', 'crosshatch', 'plus', 'rings',
  'chevron', 'diamonds', 'triangles', 'checker', 'waves', 'confetti', 'zigzag',
] as const

export type CoverPattern = (typeof COVER_PATTERNS)[number]

// White texture layer(s) for each pattern (drawn over the colour gradient).
const LAYERS: Record<CoverPattern, string> = {
  dots: 'radial-gradient(rgba(255,255,255,.16) 2px, transparent 2.5px) 0 0/20px 20px',
  'dots-lg': 'radial-gradient(rgba(255,255,255,.14) 3px, transparent 4px) 0 0/34px 34px',
  grid: 'linear-gradient(rgba(255,255,255,.13) 1px, transparent 1px) 0 0/24px 24px, linear-gradient(90deg, rgba(255,255,255,.13) 1px, transparent 1px) 0 0/24px 24px',
  diagonal: 'repeating-linear-gradient(45deg, rgba(255,255,255,.12) 0 2px, transparent 2px 12px)',
  stripes: 'repeating-linear-gradient(90deg, rgba(255,255,255,.10) 0 9px, transparent 9px 22px)',
  crosshatch: 'repeating-linear-gradient(45deg, rgba(255,255,255,.10) 0 2px, transparent 2px 13px), repeating-linear-gradient(-45deg, rgba(255,255,255,.10) 0 2px, transparent 2px 13px)',
  plus: 'repeating-linear-gradient(0deg, transparent 0 11px, rgba(255,255,255,.13) 11px 13px, transparent 13px 24px), repeating-linear-gradient(90deg, transparent 0 11px, rgba(255,255,255,.13) 11px 13px, transparent 13px 24px)',
  rings: 'repeating-radial-gradient(circle at 50% 50%, rgba(255,255,255,.12) 0 1.5px, transparent 1.5px 16px)',
  chevron: 'repeating-linear-gradient(135deg, rgba(255,255,255,.12) 0 8px, transparent 8px 16px) 0 0/16px 16px, repeating-linear-gradient(45deg, rgba(255,255,255,.12) 0 8px, transparent 8px 16px) 0 0/16px 16px',
  diamonds: 'conic-gradient(from 45deg, rgba(255,255,255,.12) 0 25%, transparent 0 50%, rgba(255,255,255,.12) 0 75%, transparent 0) 0 0/26px 26px',
  triangles: 'conic-gradient(rgba(255,255,255,.13) 0 25%, transparent 0 50%) 0 0/26px 26px',
  checker: 'conic-gradient(rgba(255,255,255,.10) 0 25%, transparent 0 50%, rgba(255,255,255,.10) 0 75%, transparent 0) 0 0/30px 30px',
  waves: 'radial-gradient(circle at 50% 120%, transparent 9px, rgba(255,255,255,.13) 10px, transparent 11px) 0 0/28px 14px',
  confetti: 'radial-gradient(rgba(255,255,255,.16) 2px, transparent 3px) 0 0/40px 40px, radial-gradient(rgba(255,255,255,.12) 2px, transparent 3px) 20px 14px/40px 40px, radial-gradient(rgba(255,255,255,.10) 1.5px, transparent 2.5px) 8px 28px/40px 40px',
  zigzag: 'repeating-linear-gradient(135deg, rgba(255,255,255,.12) 0 3px, transparent 3px 10px) 0 0/20px 20px, repeating-linear-gradient(45deg, rgba(255,255,255,.12) 0 3px, transparent 3px 10px) 0 0/20px 20px',
}

// Full CSS `background` value for a pattern in the given colour (shade gradient
// under the white texture) — for the main preview.
export function coverBackground(pattern: CoverPattern, color: string): string {
  const grad = `linear-gradient(135deg, ${color}, color-mix(in srgb, ${color} 56%, #000))`
  return `${LAYERS[pattern]}, ${grad}`
}

// Monochrome line-art version for the picker's pattern grid: dark marks on a
// light background, so the shape of the pattern reads clearly regardless of the
// chosen colour (which is picked separately, above the grid).
export function coverSwatchBackground(pattern: CoverPattern): string {
  const ink = LAYERS[pattern].replace(/rgba\(255,255,255,[^)]+\)/g, 'rgba(24,20,16,.55)')
  return `${ink}, #f1f1f3`
}
